import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Save, Tag, Search, Check } from "lucide-react";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setCategories(data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie "${c.name}" ? Les produits ne seront plus rattachés.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="ff-overline">Organisation du shop</p>
          <h1 className="font-serif text-3xl mt-2">Catégories ({categories.length})</h1>
          <p className="text-sm text-[#8A9A8E] mt-1">Créez vos propres catégories, mettez en avant des promotions, et assignez plusieurs catégories à un même produit.</p>
        </div>
        <button onClick={() => setEditing({})} className="ff-btn-primary" data-testid="admin-new-category-button">
          <Plus className="w-4 h-4" /> Nouvelle catégorie
        </button>
      </div>

      {loading ? (
        <p className="mt-8 text-[#8A9A8E]">Chargement...</p>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-categories-grid">
          {categories.map((c) => (
            <div key={c.id} className="ff-card p-5" data-testid={`admin-category-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${c.is_promo ? "bg-[#E76F51] text-white" : "bg-[#F2EFEB] text-[#2D6A4F]"}`}>
                    <Tag className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl">{c.name}</h3>
                    <p className="text-xs text-[#8A9A8E] mt-0.5">{c.product_count} produit(s)</p>
                  </div>
                </div>
                {c.is_promo && <span className="text-[10px] uppercase tracking-wider bg-[#E76F51] text-white px-2 py-1">Promo</span>}
              </div>

              <div className="mt-5 pt-4 border-t border-[#E5E3DC] flex gap-2 flex-wrap">
                <button
                  onClick={() => setManaging(c)}
                  className="text-xs px-3 py-1.5 bg-[#2D6A4F] text-white hover:bg-[#1B4332] inline-flex items-center gap-1"
                  data-testid={`admin-category-manage-${c.id}`}
                >
                  Gérer les produits
                </button>
                <button
                  onClick={() => setEditing(c)}
                  className="text-xs px-3 py-1.5 border border-[#E5E3DC] hover:bg-[#F2EFEB] inline-flex items-center gap-1"
                  data-testid={`admin-category-edit-${c.id}`}
                >
                  <Edit2 className="w-3 h-3" /> Renommer
                </button>
                <button
                  onClick={() => remove(c)}
                  className="text-xs px-3 py-1.5 border border-[#E5E3DC] text-[#D62828] hover:bg-[#D62828]/10 inline-flex items-center gap-1"
                  data-testid={`admin-category-delete-${c.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <CategoryFormModal category={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {managing && (
        <ManageProductsModal category={managing} onClose={() => setManaging(null)} onSaved={() => { setManaging(null); load(); }} />
      )}
    </div>
  );
}

function CategoryFormModal({ category, onClose, onSaved }) {
  const isNew = !category.id;
  const [form, setForm] = useState({
    name: category.name || "",
    is_promo: !!category.is_promo,
    sort_order: category.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/categories", form);
      else await api.patch(`/admin/categories/${category.id}`, form);
      toast.success(isNew ? "Catégorie créée" : "Catégorie mise à jour");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="category-form-modal">
      <div className="bg-white max-w-md w-full">
        <div className="p-6 border-b border-[#E5E3DC] flex items-center justify-between">
          <h2 className="font-serif text-2xl">{isNew ? "Nouvelle catégorie" : "Modifier la catégorie"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F2EFEB]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Nom</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="ff-input mt-1"
              data-testid="category-form-name"
              placeholder="Ex. Fruits exotiques, Réduction, Bio..."
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Ordre d'affichage</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
              className="ff-input mt-1 w-24"
              data-testid="category-form-sort"
            />
          </div>
          <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-[#E5E3DC]">
            <input
              type="checkbox"
              checked={form.is_promo}
              onChange={(e) => setForm((f) => ({ ...f, is_promo: e.target.checked }))}
              data-testid="category-form-promo"
              className="w-4 h-4"
            />
            <div>
              <span className="font-medium block">Catégorie « promotion / réduction »</span>
              <span className="text-xs text-[#8A9A8E]">Les produits seront mis en avant avec un badge orange sur la boutique.</span>
            </div>
          </label>
        </div>
        <div className="p-6 border-t border-[#E5E3DC] flex justify-end gap-3">
          <button onClick={onClose} className="ff-btn-secondary">Annuler</button>
          <button onClick={save} disabled={saving} className="ff-btn-primary" data-testid="category-form-save">
            <Save className="w-4 h-4" /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageProductsModal({ category, onClose, onSaved }) {
  const [allProducts, setAllProducts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/products");
        setAllProducts(data);
        const inCat = new Set(
          data
            .filter((p) => (p.categories || []).includes(category.name))
            .map((p) => p.id)
        );
        setSelected(inCat);
      } catch { toast.error("Erreur"); }
      finally { setLoading(false); }
    })();
  }, [category.name]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // "set" mode: replace the category membership with the selected list
      await api.post(`/admin/categories/${category.id}/products`, {
        product_ids: Array.from(selected),
        mode: "set",
      });
      toast.success(`${selected.size} produit(s) dans "${category.name}"`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally { setSaving(false); }
  };

  const filtered = allProducts.filter(
    (p) => !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="manage-products-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-[#E5E3DC] flex items-center justify-between">
          <div>
            <p className="ff-overline">Gestion des produits</p>
            <h2 className="font-serif text-2xl mt-1">{category.name}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F2EFEB]" data-testid="manage-products-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-[#E5E3DC]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A8E]" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ff-input pl-10"
              data-testid="manage-products-search"
            />
          </div>
          <p className="mt-3 text-xs text-[#8A9A8E]">
            {selected.size} produit(s) sélectionné(s). Cliquez pour ajouter/retirer.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="p-6 text-[#8A9A8E]">Chargement...</p>
          ) : (
            <ul className="divide-y divide-[#F2EFEB]">
              {filtered.map((p) => {
                const isSel = selected.has(p.id);
                return (
                  <li
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    className={`p-4 flex items-center gap-3 cursor-pointer hover:bg-[#F9F8F6] ${isSel ? "bg-[#2D6A4F]/5" : ""}`}
                    data-testid={`manage-product-${p.id}`}
                  >
                    <div className={`w-5 h-5 border flex items-center justify-center ${isSel ? "bg-[#2D6A4F] border-[#2D6A4F] text-white" : "border-[#E5E3DC]"}`}>
                      {isSel && <Check className="w-3 h-3" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-[#8A9A8E]">
                        {(p.categories || []).join(" · ") || "—"} · CHF {p.price.toFixed(2)} / {p.unit}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-6 border-t border-[#E5E3DC] flex justify-between items-center">
          <span className="text-sm text-[#4A5D4E]">{selected.size} produit(s)</span>
          <div className="flex gap-3">
            <button onClick={onClose} className="ff-btn-secondary">Annuler</button>
            <button onClick={save} disabled={saving} className="ff-btn-primary" data-testid="manage-products-save">
              <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
